import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedPlural } from 'react-intl';
import { css } from 'styled-components';
import { ETransportType } from '../../../common/types';
import { Link } from '../../../components/Link';
import { formatDuration, getRoundPrice } from '../../../helpers/results';
import { NAVIGATION } from '../../App/constants';
import {
  ALL_SPECIFIC_TABLE_HEADERS,
  EMOJI_BUS,
  EMOJI_CARPOOLING,
  EMOJI_MULTIMODAL,
  EMOJI_TRAIN,
  SPECIFIC_TABLE_BUS,
  SPECIFIC_TABLE_CARPOOLING,
  SPECIFIC_TABLE_HEADER_ALTERNATIVE,
  SPECIFIC_TABLE_HEADER_ARRIVAL,
  SPECIFIC_TABLE_HEADER_COMPANIES,
  SPECIFIC_TABLE_HEADER_DAILY_TRIPS,
  SPECIFIC_TABLE_HEADER_DEPARTURE,
  SPECIFIC_TABLE_HEADER_DURATION,
  SPECIFIC_TABLE_HEADER_FIRST_TRIP,
  SPECIFIC_TABLE_HEADER_ITINERARY,
  SPECIFIC_TABLE_HEADER_LAST_TRIP,
  SPECIFIC_TABLE_HEADER_PRICE_RANGE,
  SPECIFIC_TABLE_MULTIMODAL,
  SPECIFIC_TABLE_TRAIN,
} from '../constants';
import { SearchPageType } from '../types';
import { doesAlternativePageExist, getMainSearchPageType } from '../utils';
import messages from '../View/messages';

const SearchPageLink = ({
  children,
  destinationSlug,
  originSlug,
  pageType,
}) => {
  return (
    <Link
      customStyle={css`
        font-size: inherit;
        line-height: inherit;
        text-transform: capitalize;
      `}
      href={`${NAVIGATION.SEARCH}/${originSlug}/${destinationSlug}${
        pageType ? `/${pageType}` : ''
      }`}
    >
      {children}
    </Link>
  );
};

SearchPageLink.propTypes = {
  children: PropTypes.node.isRequired,
  destinationSlug: PropTypes.string.isRequired,
  originSlug: PropTypes.string.isRequired,
  pageType: PropTypes.string,
};

/**
 * @description Builds data for the second table containing specific information on each transport mean
 * @param {object} tripData - Trip data returned by API
 * @returns {null|{id: string, value: {[transportMean]: string|Node}}[]} Table data, each item in the array represents a row of the table,
 * and the row value contains the info for each transport means
 */
const formatSpecificTableData = (
  tripData,
  originCity,
  destinationCity,
  currentPageType,
) => {
  if (isEmpty(tripData) || !tripData.transportInformation.historyData) {
    return null;
  }
  const means = formatSpecificTableHeaders(
    tripData,
    originCity,
    destinationCity,
    currentPageType,
  );
  const transportMeansData = getSpecificTableData(
    means,
    tripData,
    originCity,
    destinationCity,
    currentPageType,
  );

  return ALL_SPECIFIC_TABLE_HEADERS.map((row) => ({
    id: row,
    value: transportMeansData[row],
  })).filter((row) => row.value);
};

const generateSpecificTableHeaderValueWrapper = ({
  destinationCity,
  isCurrentPage,
  isMainPageType,
  originCity,
  pageType,
  tripData,
}) => {
  if (
    !isCurrentPage &&
    doesAlternativePageExist({
      pageType: pageType,
      destinationCity,
      originCity,
      tripData,
      isMainPageAllowed: true,
    })
  ) {
    const Wrapper = ({ children }) => (
      <SearchPageLink
        children={children}
        destinationSlug={destinationCity.slug}
        originSlug={originCity.slug}
        pageType={isMainPageType ? '' : pageType}
      />
    );

    Wrapper.propTypes = {
      children: PropTypes.node,
    };

    return Wrapper;
  }

  const Wrapper = ({ children }) => <React.Fragment>{children}</React.Fragment>;

  Wrapper.propTypes = {
    children: PropTypes.node,
  };
  return Wrapper;
};

/**
 * @description Formats the specific table column header data by matching legacy transport means and updated ones (tripData may not contain all transport means)
 * @param {object} tripData - Trip data returned by API
 * @returns {null|{legacyId: string, id: string, value: string}[]} Array of transport means that contains data with their display value on the table (emoji)
 */
const formatSpecificTableHeaders = (
  tripData,
  originCity,
  destinationCity,
  currentPageType,
) => {
  if (isEmpty(tripData) || !tripData.transportInformation.historyData) {
    return null;
  }

  const mainPageType = getMainSearchPageType({
    destinationCity,
    originCity,
    tripData,
  });

  const BusWrapper = generateSpecificTableHeaderValueWrapper({
    tripData,
    originCity,
    destinationCity,
    pageType: SearchPageType.BUS,
    isCurrentPage: currentPageType === SearchPageType.BUS,
    isMainPageType: mainPageType === SearchPageType.BUS,
  });

  const TrainWrapper = generateSpecificTableHeaderValueWrapper({
    tripData,
    originCity,
    destinationCity,
    pageType: SearchPageType.TRAIN,
    isCurrentPage: currentPageType === SearchPageType.TRAIN,
    isMainPageType: mainPageType === SearchPageType.TRAIN,
  });

  const CarpoolingWrapper = generateSpecificTableHeaderValueWrapper({
    tripData,
    originCity,
    destinationCity,
    pageType: SearchPageType.CARPOOLING,
    isCurrentPage: currentPageType === SearchPageType.CARPOOLING,
    isMainPageType: mainPageType === SearchPageType.CARPOOLING,
  });

  const headers = {
    bus: {
      id: SPECIFIC_TABLE_BUS,
      value: (
        <BusWrapper>
          <FormattedMessage {...messages[EMOJI_BUS]} />
        </BusWrapper>
      ),
      pageType: SearchPageType.BUS,
    },
    train: {
      id: SPECIFIC_TABLE_TRAIN,
      value: (
        <TrainWrapper>
          <FormattedMessage {...messages[EMOJI_TRAIN]} />
        </TrainWrapper>
      ),
      pageType: SearchPageType.TRAIN,
    },
    multimodality: {
      id: SPECIFIC_TABLE_MULTIMODAL,
      value: <FormattedMessage {...messages[EMOJI_MULTIMODAL]} />,
      pageType: null,
    },
    carpooling: {
      id: SPECIFIC_TABLE_CARPOOLING,
      value: (
        <CarpoolingWrapper>
          <FormattedMessage {...messages[EMOJI_CARPOOLING]} />
        </CarpoolingWrapper>
      ),
      pageType: SearchPageType.CARPOOLING,
    },
  };

  return Object.entries(tripData.transportInformation)
    .filter(([id, value]) => {
      return Object.keys(headers).includes(id) && !isEmpty(value);
    })
    .map(([id]) => ({
      id: headers[id].id,
      value: headers[id].value,
      legacyId: id,
      pageType: headers[id].pageType,
    }))
    .sort((a, b) => {
      if (a.pageType === currentPageType) return -1;
      if (b.pageType === currentPageType) return 1;
      return 0;
    });
};

/**
 * @description Retrieves the data of each transport means for all rows.
 * @param {{legacyId: string, id: string, value: string}[]} means - Array of transport means containing legacyId and updated id
 * @param {object} data - transportInformation object returned by API
 * @returns {Object.<string, Object.<string, string|Node>>} - Object with one key for each row in the table, and each transport mean formatted data for each row
 */
const getSpecificTableData = (
  means,
  tripData,
  originCity,
  destinationCity,
  currentPageType,
) => {
  const mainPageType = getMainSearchPageType({
    destinationCity,
    originCity,
    tripData,
  });

  const FORMATTED_ALTERNATIVE_LINKS = {
    [ETransportType.BUS]:
      doesAlternativePageExist({
        pageType: SearchPageType.BUS,
        destinationCity,
        originCity,
        tripData,
        isMainPageAllowed: true,
      }) && currentPageType !== SearchPageType.BUS ? (
        <SearchPageLink
          children={
            <FormattedMessage
              {...messages.transportTypeTrip}
              values={{
                origin: originCity.name,
                destination: destinationCity.name,
                transportType: (
                  <FormattedMessage {...messages[ETransportType.BUS]} />
                ),
              }}
            />
          }
          destinationSlug={destinationCity.slug}
          originSlug={originCity.slug}
          pageType={
            mainPageType === SearchPageType.BUS ? '' : SearchPageType.BUS
          }
        />
      ) : null,
    [ETransportType.TRAIN]:
      doesAlternativePageExist({
        pageType: SearchPageType.TRAIN,
        destinationCity,
        originCity,
        tripData,
        isMainPageAllowed: true,
      }) && currentPageType !== SearchPageType.TRAIN ? (
        <SearchPageLink
          children={
            <FormattedMessage
              {...messages.transportTypeTrip}
              values={{
                origin: originCity.name,
                destination: destinationCity.name,
                transportType: (
                  <FormattedMessage {...messages[ETransportType.TRAIN]} />
                ),
              }}
            />
          }
          destinationSlug={destinationCity.slug}
          originSlug={originCity.slug}
          pageType={
            mainPageType === SearchPageType.TRAIN ? '' : SearchPageType.TRAIN
          }
        />
      ) : null,
    [ETransportType.CARPOOLING]:
      doesAlternativePageExist({
        pageType: SearchPageType.CARPOOLING,
        destinationCity,
        originCity,
        tripData,
        isMainPageAllowed: true,
      }) && currentPageType !== SearchPageType.CARPOOLING ? (
        <SearchPageLink
          children={
            <FormattedMessage
              {...messages.transportTypeTrip}
              values={{
                origin: originCity.name,
                destination: destinationCity.name,
                transportType: (
                  <FormattedMessage {...messages[ETransportType.CARPOOLING]} />
                ),
              }}
            />
          }
          destinationSlug={destinationCity.slug}
          originSlug={originCity.slug}
          pageType={
            mainPageType === SearchPageType.CARPOOLING
              ? ''
              : SearchPageType.CARPOOLING
          }
        />
      ) : null,
  };

  const initialData = {
    [SPECIFIC_TABLE_HEADER_PRICE_RANGE]: {},
    [SPECIFIC_TABLE_HEADER_DURATION]: {},
    [SPECIFIC_TABLE_HEADER_ITINERARY]: {},
    [SPECIFIC_TABLE_HEADER_DEPARTURE]: {},
    [SPECIFIC_TABLE_HEADER_ARRIVAL]: {},
    [SPECIFIC_TABLE_HEADER_COMPANIES]: {},
    [SPECIFIC_TABLE_HEADER_FIRST_TRIP]: {},
    [SPECIFIC_TABLE_HEADER_LAST_TRIP]: {},
    [SPECIFIC_TABLE_HEADER_DAILY_TRIPS]: {},
  };

  if (currentPageType !== SearchPageType.GENERIC) {
    initialData[SPECIFIC_TABLE_HEADER_ALTERNATIVE] = {};
  }

  const data = tripData.transportInformation;

  return means.reduce((values, mean) => {
    const stops = data[mean.legacyId].bestItinerary.minNbSteps - 1;
    const stations = formatStations(
      data[mean.legacyId].bestItinerary.stations.intermediateStations,
    );

    return {
      [SPECIFIC_TABLE_HEADER_PRICE_RANGE]: {
        ...values[SPECIFIC_TABLE_HEADER_PRICE_RANGE],
        [mean.id]: formatRange(
          `${getRoundPrice(data[mean.legacyId].bestItinerary.price.min)}€`,
          `${getRoundPrice(data[mean.legacyId].bestItinerary.price.max)}€`,
        ),
      },
      [SPECIFIC_TABLE_HEADER_DURATION]: {
        ...values[SPECIFIC_TABLE_HEADER_DURATION],
        [mean.id]: formatRange(
          formatDuration(data[mean.legacyId].bestItinerary.duration.min),
          formatDuration(data[mean.legacyId].bestItinerary.duration.max),
        ),
      },
      [SPECIFIC_TABLE_HEADER_ITINERARY]: {
        ...values[SPECIFIC_TABLE_HEADER_ITINERARY],
        [mean.id]:
          stops === 0 ? (
            <FormattedMessage {...messages.mainTableDirect} />
          ) : (
            itineraryStations(stops, stations)
          ),
      },
      [SPECIFIC_TABLE_HEADER_DEPARTURE]: {
        ...values[SPECIFIC_TABLE_HEADER_DEPARTURE],
        [mean.id]: data[mean.legacyId].bestItinerary.stations.origin.name,
      },
      [SPECIFIC_TABLE_HEADER_ARRIVAL]: {
        ...values[SPECIFIC_TABLE_HEADER_ARRIVAL],
        [mean.id]: data[mean.legacyId].bestItinerary.stations.destination.name,
      },
      [SPECIFIC_TABLE_HEADER_COMPANIES]: {
        ...values[SPECIFIC_TABLE_HEADER_COMPANIES],
        [mean.id]: formatCompanies(data[mean.legacyId].bestItinerary.companies),
      },
      [SPECIFIC_TABLE_HEADER_FIRST_TRIP]: {
        ...values[SPECIFIC_TABLE_HEADER_FIRST_TRIP],
        [mean.id]: formatTableDuration(
          data[mean.legacyId].bestItinerary.departureTsOfFirst,
        ),
      },
      [SPECIFIC_TABLE_HEADER_LAST_TRIP]: {
        ...values[SPECIFIC_TABLE_HEADER_LAST_TRIP],
        [mean.id]: formatTableDuration(
          data[mean.legacyId].bestItinerary.departureTsOfLast,
        ),
      },
      [SPECIFIC_TABLE_HEADER_DAILY_TRIPS]: {
        ...values[SPECIFIC_TABLE_HEADER_DAILY_TRIPS],
        [mean.id]: data[mean.legacyId].nbPerDay,
      },
      [SPECIFIC_TABLE_HEADER_ALTERNATIVE]: values[
        SPECIFIC_TABLE_HEADER_ALTERNATIVE
      ]
        ? {
            ...values[SPECIFIC_TABLE_HEADER_ALTERNATIVE],
            [mean.id]: FORMATTED_ALTERNATIVE_LINKS[mean.legacyId],
          }
        : values[SPECIFIC_TABLE_HEADER_ALTERNATIVE],
    };
  }, initialData);
};
/**
 * @description Stringify an array of companies
 * @param {{fullname: string}[]} companies - Companies involved in a trip
 * @returns {string}
 */
const formatCompanies = (companies) =>
  companies.map((company) => company.fullname).join(', ');

/**
 * @description Stringify an array of stations
 * @param {{name: string}[]} stations - Stations involved in a trip
 * @returns {string}
 */
const formatStations = (stations) =>
  stations.map((company) => company.name).join(', ');

/**
 * @description Formats table durations
 * @param {number} duration - Duration of the trip in minutes
 * @returns {Node} Bold and formatted duration
 */
const formatTableDuration = (duration) => (
  <strong>{formatDuration(duration)}</strong>
);

/**
 * @description Beautify a table range
 * @param {string|number} min - Min value
 * @param {string|number} max - Max value
 * @returns {Node} Formatted range
 */
const formatRange = (min, max) => (
  <p>
    <strong>{min}</strong>
    &nbsp;-&nbsp;
    <strong>{max}</strong>
  </p>
);

/**
 * @description Formats all possible stations, handling single or multiple stations
 * @param {number} stopsNb - Number of stops
 * @param {string} stations - Stringified list of stations
 * @returns {FormattedPlural} Number of stops with corresponding stations
 */
const itineraryStations = (stopsNb, stations) => {
  if (!stations) {
    return (
      <FormattedMessage
        {...messages.specificTableStopUnknownStations}
        values={{ stopsNb }}
      />
    );
  }

  return (
    <FormattedPlural
      one={
        <FormattedMessage
          {...messages.specificTableStop}
          values={{ stopsNb, stations }}
        />
      }
      other={
        <FormattedMessage
          {...messages.specificTableStops}
          values={{ stopsNb, stations }}
        />
      }
      value={stopsNb}
    />
  );
};

export { formatSpecificTableData, formatSpecificTableHeaders };
